/**
 * <CollapsedToolJson> — fallback renderer for any `tool_use` block the
 * <ToolUseBlock> dispatcher doesn't have a custom card for. Renders
 * the tool name + a collapsible JSON view of `input`. NEVER silently
 * hides a tool call — visibility is the whole point: future tool
 * names show up as "what is this?" cards rather than invisible.
 *
 * Adding a custom render: extend <ToolUseBlock> with a new
 * `block.name === "..."` branch that returns the dedicated component;
 * this fallback then stops firing for that name.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GENERIC_TOOL_LABEL_KEY } from "@/lib/chat/tool-activity";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

interface CollapsedToolJsonProps {
  block: ToolUseBlockType;
}

export function CollapsedToolJson({ block }: CollapsedToolJsonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Pretty-print the input; trim if very large for the closed preview.
  let json = "";
  try {
    json = JSON.stringify(block.input, null, 2);
  } catch {
    json = String(block.input ?? "");
  }
  return (
    <div
      data-testid="collapsed-tool-json"
      data-tool-name={block.name}
      className="my-1 rounded-md border border-cream-300 bg-cream-100/30 text-body-sm dark:border-coffee-700 dark:bg-coffee-900/30"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-caption font-medium uppercase tracking-wide text-coffee-600 dark:text-cream-400"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Wrench size={12} />
        {/* ⚠ Presentable label, never the raw identifier (operator directive
            2026-08-14). The raw name stays one attribute up in `data-tool-name`
            — dev tooling and tests read that; the lawyer reads this. */}
        <span>
          {t(`chat.activity.trailLabel.${block.name}`, {
            defaultValue: t(GENERIC_TOOL_LABEL_KEY),
          })}
        </span>
      </button>
      {open && (
        <pre
          data-testid="collapsed-tool-json-body"
          className="overflow-x-auto whitespace-pre-wrap border-t border-cream-300 px-3 py-2 font-mono text-coffee-700 dark:border-coffee-700 dark:text-cream-300"
        >
          {json}
        </pre>
      )}
    </div>
  );
}
